import { CSSObject } from '@emotion/react'
import {
  Color,
  CornerRadius,
  FocusIndicator,
  FontWeight,
  LineWeight,
  Shadow,
  ShadowElevation,
  Spacing,
  Theme,
  Timing,
  Transition,
  Typography,
  wideGamutColor,
} from '@opallabs/gem'

const classNamePrefix = 'lapidary-calendar--'
export const selectors = {
  container: `${classNamePrefix}container`,
  date: `${classNamePrefix}date`,
  footer: `${classNamePrefix}footer`,
  header: `${classNamePrefix}header`,
  headerDay: `${classNamePrefix}headerDay`,
  monthContainer: `${classNamePrefix}monthContainer`,
  table: `${classNamePrefix}table`,
  week: `${classNamePrefix}week`,
} as const

interface ThemeProp {
  theme: Theme
}

export const container = {
  ...Typography.T500,
  ...Shadow[ShadowElevation.E2],
  borderRadius: CornerRadius.Large,
  boxSizing: 'border-box',
  cursor: 'auto',
  inlineSize: 320,

  [`.${selectors.monthContainer}`]: {
    padding: Spacing.L,
  },
  [`.${selectors.table}`]: {
    borderSpacing: 0,
    borderBlockStartStyle: 'solid',
    borderBlockStartWidth: LineWeight.Large,
    // `<td>` is `display: flex` so we can control the `block-size`. (`block-size` is ignored with the default
    // `display: table-cell`.) This forces each `<td>` onto its own line, so we need additional styling to fix the
    // layout. The best option--especially given that we want space between cells--is making `<table>` `display: grid`
    // and propagating down to `<td>` by setting `display: contents` on `<thead>`, `<tbody>`, and `<tr>`. (Flexbox
    // ignores `border-spacing`, so getting space between `<td>`s requires styling at some higher level. If we didn't
    // want the space between cells we could just make `<td>` `display: inline-flex` instead.)
    display: 'grid',
    gap: Spacing.S,
    gridTemplateColumns: 'repeat(7, 1fr)',

    paddingBlockStart: Spacing.S,
  },
  [`.${selectors.table} tbody, .${selectors.header}, .${selectors.week}`]: {
    // See discussion on `display: 'grid'`
    display: 'contents',
  },
  [`.${selectors.date}, .${selectors.headerDay}`]: {
    alignItems: 'center',
    blockSize: Spacing.M * 2,
    borderRadius: CornerRadius.Small,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    // Border always needs to be present so layout doesn't shift for states that use it
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: LineWeight.Small,
    inlineSize: Spacing.M * 2,
    justifyContent: 'center',
    paddingBlock: Spacing.XS * 1.5,
    paddingInline: Spacing.XS,
  },
  [`.${selectors.headerDay}`]: {
    cursor: 'default',
    fontWeight: FontWeight.Bold,
  },
  [`.${selectors.date}`]: {
    ...Transition(
      ['background-color', 'border-color', 'box-shadow', 'color', 'opacity'],
      {
        duration: Timing.Fast,
      }
    ),

    '&:not(.is--sameMonth)': {
      span: {
        opacity: 0.7,
      },
    },
    '&:not(.is--allowed)': {
      pointerEvents: 'none',

      span: {
        opacity: 0.5,
      },
    },
  },
  [`.${selectors.footer}`]: {
    borderEndEndRadius: CornerRadius.Small,
    borderEndStartRadius: CornerRadius.Small,
    display: 'flex',
    padding: Spacing.XS * 3,
  },
} as const satisfies CSSObject

export const dynamic = (theme: Theme) =>
  ({
    backgroundColor: Color[theme]['Color/Surface/Surface-1'],
    color: Color[theme]['Text/Primary'],

    [`.${selectors.table}`]: {
      borderBlockStartColor: wideGamutColor(
        Color[theme]['Strokes/Secondary-4'],
        0.08
      ),
    },
    [`.${selectors.date}`]: {
      '&:focus': {
        ...FocusIndicator[theme],
      },
      '&:hover': {
        backgroundColor: Color[theme]['Color/Primary/Primary-1'],
        borderColor: Color[theme]['Strokes/Primary-2'],
        color: Color[theme]['Text/Links'],
      },
      '&.is--today': {
        backgroundColor: Color[theme]['Color/Surface/Surface-2'],
        fontWeight: FontWeight.Bold,
      },
      '&:not(.is--sameMonth)': {
        color: Color[theme]['Text/Secondary'],
      },
      '&:active, &.is--selected': {
        backgroundColor: Color[theme]['Color/Primary/Primary-6'],
        color: Color[theme]['Text/Contrast'],
      },
      '&:not(.is--allowed)': {
        backgroundColor: Color[theme]['Color/Surface/Surface-2'],
        color: Color[theme]['Text/Secondary'],
      },
      '&:not(.is--allowed).is--selected': {
        backgroundColor: Color[theme]['Icons/Error'],
      },
      '&.is--allowed:not(.is--strict-allowed)': {
        '&:hover': {
          borderColor: Color[theme]['Strokes/Error'],
          color: Color[theme]['Text/Warning'],
        },
        '&:active, &.is--selected': {
          borderColor: 'transparent',
          backgroundColor: Color[theme]['Color/Warning/Warning-1'],
          color: Color[theme]['Text/Primary'],
        },
      },
    },
    [`.${selectors.footer}`]: {
      backgroundColor: Color[theme]['Color/Surface/Surface-2'],
      borderBlockStart: `1px solid ${Color[theme]['Strokes/Secondary-1']}`,
    },
  }) satisfies CSSObject

export const removeDate = ({ theme }: ThemeProp): CSSObject => ({
  ...Shadow[ShadowElevation.E2],
  color: Color[theme].Text.Warning,
  marginBlockStart: 4,
  minBlockSize: 40,
  inlineSize: '100%',

  ':disabled': {
    opacity: 1,
  },
})

export const disabledText = (isDisabled: boolean): CSSObject => ({
  opacity: isDisabled ? 0.5 : 1,
})

export const softDisallowContainer = ({ theme }: ThemeProp): CSSObject => ({
  ...Typography.T300,

  display: 'flex',
  alignItems: 'center',
  gap: Spacing.XS,
  color: Color[theme]['Text/Error'],
  marginBlockStart: Spacing.S,
})
